import { Injectable, Injector } from '@angular/core';
import { ReducerManager, Store } from '@ngrx/store';
import { EffectsRootModule, Actions, EffectSources } from '@ngrx/effects';
import { Blueprint, InitType } from '../cyanprints/blueprint';
import { StateServices } from './i-state-services';
import { PostBlueprint } from '../cyanprints/post/post';
import { EventsWebsocketService } from '../../../services/entities/events-websocket.service';
import { Entity } from 'src/app/state/entities/entity.model';
import { Vector3 } from 'three/src/math/Vector3.js';
import { RenderServices } from './i-render-services';
import { PixiService } from '../../rendering/pixi/pixi-service';
import { ThreeService } from '../../rendering/threejs/three.service';
import { SurfaceService } from '../../surface/surface.service';
import { TimerBlueprint } from '../cyanprints/timer/timer';
import {NetworkServices} from "./i-network-services";
import {UsersService} from "../../../services/users.service";
import {UserAvatarBlueprint} from "../cyanprints/user-avatar/user-avatar";
import {IMetadata} from "./i-metadata";

import * as authSelectors from '../../../state/auth/auth.selector';
import {FilesService} from "../../../services/files/files.service";
import {Router} from "@angular/router";
import {ReactionsService} from "../../../services/reactions.service";
import {ShareService} from "../../../services/share.service";
import {MapReactionBlueprint} from "../cyanprints/map-reaction/map-reaction";
import {GalleryBlueprint} from "../cyanprints/gallery/gallery";
import {TextUtilsService} from "../../../services/text-utils.service";
import {IsolateEntityService} from "../../tests/events/isolate-entity.service";

export type BlueprintConstructor<T extends Blueprint = Blueprint> =
  new (...args: any[]) => T;

@Injectable({
  providedIn: 'root'
})
export class InstanceService {

  private instances = new Map<string, Blueprint>();
  stateServices: StateServices = {}
  renderServices: RenderServices = {}
  networkServices: NetworkServices = {}
  blueprintRegister = new Map<string, BlueprintConstructor>();
  ownerId: string
  isolateEntityId: string = null

  constructor(
    private store: Store,
    private websocketService: EventsWebsocketService,
    private reducerManager: ReducerManager,
    private effectSources: EffectSources,
    private userService: UsersService,
    private injector: Injector,
    private actions$: Actions,
    private fileService: FilesService,
    private threeService: ThreeService,
    private surfaceService: SurfaceService,
    private pixiService: PixiService,
    private reactionsService: ReactionsService,
    private shareService: ShareService,
    private textUtilsService: TextUtilsService,
    private router: Router,
    private isolateService: IsolateEntityService
  ) {
    isolateService.isolateId.subscribe((id) => {
      this.isolateEntityId = id
    })
    this.networkServices.userService = this.userService
    this.networkServices.fileService = this.fileService
    this.networkServices.websocketService = this.websocketService
    this.networkServices.reactionsService = this.reactionsService
    this.networkServices.shareService = this.shareService
    this.stateServices.reducerManager = this.reducerManager
    this.stateServices.store = this.store
    this.stateServices.websocketService = this.websocketService
    this.stateServices.effectSources = this.effectSources
    this.stateServices.injector = this.injector
    this.stateServices.actions$ = this.actions$
    this.stateServices.router = this.router
    this.renderServices.fileService = this.fileService;
    this.renderServices.threeService = this.threeService
    this.renderServices.surfaceService = this.surfaceService
    this.renderServices.pixiService = this.pixiService
    this.renderServices.textUtilsService = this.textUtilsService
    this.register("Image", PostBlueprint)
    this.register("Timer", TimerBlueprint)
    this.register("UserAvatar", UserAvatarBlueprint)
    this.register("MapReaction", MapReactionBlueprint)
    this.register("Gallery", GalleryBlueprint)
    this.store.select(authSelectors.selectUser).subscribe((user) => {
      this.ownerId = user?.id
    })
  }

  register<T extends Blueprint>(type: string, cls: BlueprintConstructor<T>): void {
    this.blueprintRegister.set(type, cls);
  }

  instance(config: {
    entity: Entity,
    initType: InitType
  }) {
    if (this.isolateEntityId) {
      if (this.isolateEntityId !== config.entity.id) {
        return
      }
    }
    if (this.exists(config.entity.id)) {
      return
    }
    const blueprintConstructor = this.blueprintRegister.get(config.entity.type)
    if (!blueprintConstructor) {
      return
    }
    const instance = new blueprintConstructor()
    const metadata: IMetadata = {
      isOwners: config.entity.ownerId === this.ownerId
    }
    instance.init({
      entity: config.entity,
      initType: config.initType,
      stateServices: this.stateServices,
      renderServices: this.renderServices,
      networkServices: this.networkServices,
      metadata: metadata
    })
    this.instances.set(config.entity.id, instance)
  }

  exists(entityId: string) {
    const entity = this.instances.get(entityId)
    if (entity) {
      return true
    }
    return false
  }

  moveInstance(config: {
    instanceId: string, 
    newPos: Vector3,
    oldPos?: Vector3
  }) {
    const instance = this.instances.get(config.instanceId)
    if (!instance) {
      return
    }
    instance.updatePosition({
      newPos: config.newPos
    })
  }

  removeInstance(entityId: string) {
    const instance = this.instances.get(entityId)
    instance.destroy()
    this.instances.delete(entityId)
  }

  // createPost() {
  //   const post = new PostBlueprint()
  //   post.init({
  //     entityId: "posttest1",
  //     initType: InitType.Created,
  //     stateServices: this.stateServices
  //   })
  // }
}
